import {
  type QueryClient,
  type UseQueryResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { REACT_QUERY_STALE_TIME } from "src/constants";
import { useGetCurrentCustomerQuery } from "src/graphql-next/generated/queries";
import type {
  CurrentCustomerFieldsFragment,
  GetCurrentCustomerQuery,
} from "src/graphql-next/generated/types";
import {
  type AccountFragment,
  BrandCodeEnum,
} from "src/graphql/generated/types";
import { useBrandStore } from "src/stores/brand-store";

import { useCurrentAccount } from "./useCurrentAccount";
import { useDisplayedAccountId } from "./useDisplayedAccountId";

export type CurrentCustomer = AccountFragment["customer"] &
  Pick<CurrentCustomerFieldsFragment, "shouldShowInformationUpdateBanner">;

export function useCurrentCustomer(): CurrentCustomer {
  const { customer } = useCurrentAccount();
  const { data } = useCurrentCustomerQuery();

  // Without the ms-customer part (failed lookup, other brand), the customer is not
  // invited to anything.
  return {
    ...customer,
    shouldShowInformationUpdateBanner:
      data?.account.customer.shouldShowInformationUpdateBanner ?? false,
  };
}

export function useCurrentCustomerQuery(): UseQueryResult<GetCurrentCustomerQuery> {
  const accountId = useDisplayedAccountId();
  // Only the Foncia banner reads ms-customer so far: drop this gate once a field
  // serves every brand.
  const isFoncia = useBrandStore(
    (state) => state.brandCode === BrandCodeEnum.Foncia,
  );

  return useGetCurrentCustomerQuery(
    { accountId: accountId ?? "" },
    {
      enabled: isFoncia && accountId !== undefined,
      staleTime: REACT_QUERY_STALE_TIME,
      retry: 1,
      // The pages CurrentAccountWrapper mounts after a failed lookup would otherwise
      // refetch it and send the wrapper back to its loader, in a loop.
      retryOnMount: false,
    },
  );
}

export function useInvalidateCurrentCustomer(): () => Promise<void> {
  const queryClient = useQueryClient();

  // Plato's mirror lags behind a profile update, so refetching the account now would
  // overwrite the values the caller patched into its cache with stale ones. Every
  // account of the customer caches its own lookup, so all of them are refreshed.
  return useCallback(
    () => invalidateCurrentCustomers(queryClient),
    [queryClient],
  );
}

export function invalidateCurrentCustomers(
  queryClient: QueryClient,
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: currentCustomerQueriesKey,
  });
}

const currentCustomerQueriesKey = useGetCurrentCustomerQuery
  .getKey({ accountId: "" })
  .slice(0, 1);
